// src/views/admin/ADParents.jsx
import { useState, useEffect } from "react";
import { useAdminService } from "../../services/adminService";
import { Loader } from "../../components/Loader";
import { useToastHook } from "../../utils/useToast";
import { urlimage } from "../../utils/config";

// ─── HELPER POUR LES URLs D'IMAGES ──────────────────────────────────────────
const getImageUrl = (path) => {
  if (!path) return null;
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  if (path.startsWith('/')) return `${urlimage}${path}`;
  return `${urlimage}/${path}`;
};

function Modal({ title, onClose, children, maxWidth = 560 }) {
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal-box" style={{ maxWidth }}>
        <div className="modal-title">
          {title}
          <button className="ibtn" style={{ border: "none" }} onClick={onClose}>
            <i className="ti ti-x" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── HELPERS ──────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("fr-FR");
  } catch {
    return dateStr;
  }
};

const getGenreLabel = (genre) => {
  if (genre === "M") return "Masculin";
  if (genre === "F") return "Féminin";
  return genre || "—";
};

// Classe la plus récente parmi les inscriptions d'un enfant (même logique
// que dans ADEleves.jsx pour rester cohérent partout dans l'admin).
const getCurrentClasse = (eleve) => {
  if (!eleve.inscriptions || eleve.inscriptions.length === 0) return "Non inscrit";
  const sorted = [...eleve.inscriptions].sort((a, b) => {
    const dateA = new Date(a.annee_scolaire_detail?.date_debut || a.date_inscription || 0);
    const dateB = new Date(b.annee_scolaire_detail?.date_debut || b.date_inscription || 0);
    return dateB - dateA;
  });
  return sorted[0]?.classe_nom || "Non renseigné";
};

export default function ADParents({ slug }) {
  const [parents, setParents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedLoading, setSelectedLoading] = useState(false);

  const toast = useToastHook();
  const { getParents, getParentDetail } = useAdminService();

  useEffect(() => {
    if (slug) {
      loadData();
    }
  }, [slug]);

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await getParents(slug, { search });
      setParents(data.results || data);
    } catch (error) {
      toast.error("Impossible de charger les parents");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (slug) {
        getParents(slug, { search })
          .then(data => setParents(data.results || data))
          .catch(() => {});
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openDetail = async (parent) => {
    setSelected(parent);
    setModal("detail");
    setSelectedLoading(true);
    try {
      const data = await getParentDetail(slug, parent.id);
      setSelected(data);
    } catch (error) {
      toast.error("Impossible de charger les détails du parent");
    } finally {
      setSelectedLoading(false);
    }
  };

  const displayed = parents;

  if (loading) return <Loader />;

  return (
    <div>
      {/* Barre d'outils */}
      <div className="sb2">
        <input
          type="text"
          placeholder="Rechercher un parent (nom, téléphone, enfant)..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ minWidth: 300 }}
        />
        <button className="bsec" onClick={loadData}>
          <i className="ti ti-refresh" /> Actualiser
        </button>
      </div>

      <div style={{ display: "flex", gap: "16px", marginBottom: "14px", fontSize: "14px", color: "var(--m)" }}>
        <span>Total: <strong style={{ color: "var(--t)" }}>{parents.length}</strong></span>
      </div>

      {/* Tableau */}
      <div className="card" style={{ padding: 0, overflow: "hidden" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th style={{ width: "20%" }}>Nom complet</th>
              <th style={{ width: "18%" }}>Email</th>
              <th style={{ width: "18%" }}>Téléphone 1</th>
              <th style={{ width: "18%" }}>Téléphone 2</th>
              <th style={{ width: "16%" }}>Enfants</th>
              <th style={{ width: "10%" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayed.map(p => {
              const enfants = p.enfants || [];
              return (
                <tr key={p.id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>
                      {p.first_name} {p.last_name}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--m)" }}>@{p.username}</div>
                  </td>
                  <td style={{ color: "var(--m)", fontSize: 13 }}>{p.email || "—"}</td>
                  <td style={{ color: "var(--m)", fontSize: 13 }}>{p.telephone1 || "—"}</td>
                  <td style={{ color: "var(--m)", fontSize: 13 }}>{p.telephone2 || "—"}</td>
                  <td>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                      {enfants.slice(0, 2).map(e => (
                        <span key={e.id} className="pill neu" style={{ fontSize: 11 }}>
                          {e.prenom} {e.nom}
                        </span>
                      ))}
                      {enfants.length > 2 && (
                        <span className="pill neu" style={{ fontSize: 11 }}>
                          +{enfants.length - 2}
                        </span>
                      )}
                      {enfants.length === 0 && (
                        <span style={{ fontSize: 12, color: "var(--h)" }}>Aucun enfant</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="act">
                      {/* Détails - Bleu turquoise comme dans ADEleves */}
                      <button 
                        onClick={() => openDetail(p)} 
                        title="Voir détails"
                        style={{ background: "#17a2b8", color: "white" }}
                        onMouseEnter={e => { e.currentTarget.style.opacity = "0.8"; }}
                        onMouseLeave={e => { e.currentTarget.style.opacity = "1"; }}
                      >
                        <i className="ti ti-eye" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
            {displayed.length === 0 && (
              <tr>
                <td colSpan="6" style={{ textAlign: "center", color: "var(--h)", padding: 40 }}>
                  {search
                    ? "Aucun parent ne correspond à votre recherche."
                    : "Aucun parent enregistré."}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal Détail — infos complètes du parent + de chaque enfant (classe, abonnement...) */}
      {modal === "detail" && selected && (
        <Modal title="Détail du parent" onClose={() => setModal(null)} maxWidth={640}>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <div style={{ fontSize: 17, fontWeight: 700 }}>
              {selected.first_name} {selected.last_name}
            </div>
            <div style={{ fontSize: 13, color: "var(--m)", marginBottom: 4 }}>
              @{selected.username}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              <div><span style={{ color: "var(--m)" }}>Email :</span> {selected.email || "—"}</div>
              <div><span style={{ color: "var(--m)" }}>Téléphone 1 :</span> {selected.telephone1 || "—"}</div>
              <div><span style={{ color: "var(--m)" }}>Téléphone 2 :</span> {selected.telephone2 || "—"}</div>
              <div><span style={{ color: "var(--m)" }}>Rôle :</span> <span className="pill info">Parent</span></div>
            </div>

            <div style={{ marginTop: 8 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--t)", marginBottom: 8 }}>
                Enfants ({selected.enfants?.length || 0})
              </div>

              {selectedLoading ? (
                <div style={{ fontSize: 13, color: "var(--h)" }}>Chargement...</div>
              ) : selected.enfants && selected.enfants.length > 0 ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {selected.enfants.map(e => (
                    <div key={e.id} style={{
                      display: "flex", gap: 12,
                      padding: "10px 12px", background: "var(--s2)", borderRadius: 8,
                      border: "1px solid var(--b)",
                    }}>
                      {e.photo ? (
                        <img
                          src={getImageUrl(e.photo)}
                          alt={`${e.prenom} ${e.nom}`}
                          style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                        />
                      ) : (
                        <div style={{
                          width: 52, height: 52, borderRadius: "50%", flexShrink: 0,
                          background: "var(--b)", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 20, color: "var(--h)"
                        }}>
                          <i className="ti ti-user" />
                        </div>
                      )}

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{e.prenom} {e.nom}</div>
                          <span style={{
                            fontFamily: "'Space Grotesk',sans-serif",
                            fontWeight: 700, fontSize: 11,
                            background: "var(--bl-bg)", color: "var(--bl)",
                            padding: "2px 7px", borderRadius: 5, flexShrink: 0,
                          }}>
                            {e.matricule}
                          </span>
                        </div>

                        <div style={{ fontSize: 12, color: "var(--m)", marginTop: 3, display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
                          <span>{getGenreLabel(e.genre)}</span>
                          <span>·</span>
                          <span>{formatDate(e.date_naissance)}</span>
                          <span>·</span>
                          <span>Classe : {getCurrentClasse(e)}</span>
                        </div>

                        <div style={{ marginTop: 6 }}>
                          {e.abonnement_actif ? (
                            <div style={{ fontSize: 12, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                              <span className={`pill ${e.abonnement_actif.est_valide ? "ok" : "pu"}`}>
                                {e.abonnement_actif.est_valide ? "Abonnement actif" : "Abonnement expiré"}
                              </span>
                              <span style={{ color: "var(--m)" }}>
                                {e.abonnement_actif.offre_nom} — jusqu'au {formatDate(e.abonnement_actif.date_fin)}
                              </span>
                            </div>
                          ) : (
                            <span style={{ fontSize: 12, color: "var(--h)" }}>Aucun abonnement actif</span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{ fontSize: 13, color: "var(--h)" }}>Aucun enfant associé à ce parent</div>
              )}
            </div>
          </div>
          <div className="factions">
            <button className="bsec" onClick={() => setModal(null)}>Fermer</button>
          </div>
        </Modal>
      )}

      <style>{`
        @keyframes spin { 
          0% { transform: rotate(0deg); } 
          100% { transform: rotate(360deg); } 
        }

        /* Styles des boutons d'action */
        .act button {
          width: 30px;
          height: 30px;
          border-radius: 4px;
          border: none;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 14px;
          transition: all .2s;
        }

        .act button:hover {
          opacity: 0.8 !important;
        }
      `}</style>
    </div>
  );
}